import { useEffect, useId, useRef, type ReactNode } from 'react';

const dialogStack: HTMLElement[] = [];
let bodyOverflow = '';

export function Dialog({ open, title, children, onClose, size = 'default' }: { open: boolean; title: string; children: ReactNode; onClose: () => void; size?: 'default' | 'wide' }) {
  const titleId = useId();
  const panel = useRef<HTMLDivElement>(null);
  const restore = useRef<HTMLElement | null>(null);
  const close = useRef(onClose);
  close.current = onClose;
  useEffect(() => {
    if (!open || !panel.current) return;
    const element = panel.current;
    if (!dialogStack.length) bodyOverflow = document.body.style.overflow;
    dialogStack.push(element);
    restore.current = document.activeElement as HTMLElement;
    document.body.style.overflow = 'hidden';
    panel.current?.querySelector<HTMLElement>('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])')?.focus();
    function keydown(event: KeyboardEvent) {
      if (dialogStack.at(-1) !== element) return;
      if (event.key === 'Escape') { event.preventDefault(); close.current(); return; }
      if (event.key !== 'Tab' || !panel.current) return;
      const focusable = Array.from(panel.current.querySelectorAll<HTMLElement>('button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'));
      if (!focusable.length) return;
      const first = focusable[0], last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
    }
    document.addEventListener('keydown', keydown);
    return () => {
      document.removeEventListener('keydown', keydown);
      const wasTop = dialogStack.at(-1) === element;
      dialogStack.splice(dialogStack.indexOf(element), 1);
      if (!dialogStack.length) document.body.style.overflow = bodyOverflow;
      if (wasTop && restore.current?.isConnected) restore.current.focus();
    };
  }, [open]);
  if (!open) return null;
  return <div className="dialog-backdrop" onMouseDown={event => { if (event.target === event.currentTarget) onClose(); }}>
    <div ref={panel} className={`record-dialog${size === 'wide' ? ' record-dialog--wide' : ''}`} role="dialog" aria-modal="true" aria-labelledby={titleId}>
      <div className="dialog-heading"><h2 id={titleId}>{title}</h2><button type="button" aria-label="Закрыть" onClick={onClose}>×</button></div>
      {children}
    </div>
  </div>;
}
